import { api } from "./api";

// ---- the planning job queue ------------------------------------------------
// A solve, diagnosis or swap is queued on the server and run by the planning
// worker, never inside the request: the POST answers 202 with the job, and
// the answer arrives at the job's result URL. `waitForJob` hides that, so
// callers still `await planning.solve(...)` and get the plan — or the same
// ApiError (400, 409 …) the endpoint used to throw.

const ACTIVE = ["queued", "running"];
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const getPlanningJob = (jobId) => api.get(`/api/planning/jobs/${jobId}`);
/** A finished job's answer (a colleague's plan stays readable for a day). */
export const getPlanningJobResult = (jobId) =>
  api.get(`/api/planning/jobs/${jobId}/result`);
export const cancelPlanningJob = (jobId) =>
  api.post(`/api/planning/jobs/${jobId}/cancel`);
/** Who holds the unit's lanes, queue positions, progress, the last finished
 *  re-plan and whether the planning service is up. */
export const getPlanningLocks = (unit) =>
  api.get("/api/planning/locks", { params: { unit } });

export async function waitForJob(accepted, { intervalMs = 1000 } = {}) {
  let job = accepted.job ?? { status: accepted.status };
  while (ACTIVE.includes(job.status)) {
    await sleep(job.status === "queued" ? intervalMs * 1.5 : intervalMs);
    job = await getPlanningJob(accepted.job_id);
  }
  // Throws the job's own refusal as an ApiError, exactly like a sync call.
  return api.get(accepted.result);
}

export const solve = async (unit, request) =>
  waitForJob(await api.post("/api/solve", request, { params: { unit } }));

/** Long optimizations: start a job, then poll until it leaves "running". */
export const solveAsync = (unit, request) =>
  api.post("/api/solve/async", request, { params: { unit } });

/** Propose running `replacement_wo` in `blocked_wo`'s slot: pins the
 *  replacement there and re-solves so the engine re-places the blocked
 *  batch. Returns the proposed schedule — it does NOT commit, so the
 *  planner reviews the Gantt and signs the commit separately. */
export const swapBatch = async (unit, { blockedWo, replacementWo, reason }) =>
  waitForJob(
    await api.post(
      "/api/solve/swap",
      { blocked_wo: blockedWo, replacement_wo: replacementWo, reason },
      { params: { unit } },
    ),
  );
export const getSolveJob = (jobId) => api.get(`/api/solve/async/${jobId}`);

/** Infeasibility triage: takes the SAME body as /api/solve, confirms the
 *  failure with a short re-run, then re-solves under up to three
 *  single-lever relaxations (pins released, horizon extended, all
 *  equipment available) and names the lever that actually restores
 *  feasibility. Queued like a solve; wall time is capped at ~30 s. */
export const diagnoseSolve = async (unit, request) =>
  waitForJob(await api.post("/api/solve/diagnose", request, { params: { unit } }));

/** Constraint-checks a schedule the planner adjusted by hand on the Gantt. */
export const validateSchedule = (unit, schedule, context = {}) =>
  api.post("/api/validate", { schedule, context }, { params: { unit } });

// The study endpoint reads its options from the QUERY string (the body
// was silently ignored — the old binding posted time_limit_s there and
// every study ran at the default depth).
export const startDebottleneckStudy = (unit, { time_limit_s, mode } = {}) =>
  api.post("/api/debottleneck/study", undefined, {
    params: { unit, time_limit_s, mode },
  });
export const getDebottleneckStudy = (jobId) =>
  api.get(`/api/debottleneck/study/${jobId}`);

// The unit's most recent published optimization, committed or not — the
// planner board's fallback when nothing has been committed yet.
export const getLastSchedule = (unit) =>
  api.get("/api/schedule/last", { params: { unit } });

/** Optimization Advisor: candidate schedule moves, each priced by a REAL
 *  re-solve against a baseline run — measured deltas, never estimates.
 *  Same job pattern as the debottleneck study. `goal` picks the objective
 *  preset the whole study (baseline + variants) runs under:
 *  "balanced" (default) | "on_time_first" | "cleaning_first". */
export const startAdvisor = (unit, { time_limit_s, goal } = {}) =>
  api.post("/api/advisor/run", undefined, {
    params: { unit, time_limit_s, goal },
  });
export const getAdvisorJob = (jobId) => api.get(`/api/advisor/run/${jobId}`);

/** The unit's last few completed advisor runs — compact, metrics-only
 *  records (goal, baseline KPIs, per-candidate deltas), newest first. */
export const getAdvisorHistory = (unit) =>
  api.get("/api/advisor/history", { params: { unit } });

/** The unit's fact fingerprint as of NOW, with plant-language names —
 * the board compares it against the solve's own to disclose staleness
 * before the commit gate would refuse it. */
export const currentFacts = (unit) =>
  api.get("/api/facts", { params: { unit } });

/** The server-side scenario shelf: named solved-but-not-committed
 * schedules that survive reloads and compare like committed versions. */
export const getScenarioShelf = (unit) =>
  api.get("/api/scenarios/shelf", { params: { unit } });
export const saveScenarioShelf = (unit, payload) =>
  api.post("/api/scenarios/shelf", payload, { params: { unit } });
export const deleteScenarioShelf = (unit, id) =>
  api.del("/api/scenarios/shelf", { params: { unit }, body: { id } });
export const compareScenarioShelf = (unit, a, b) =>
  api.get("/api/scenarios/shelf/compare", { params: { unit, a, b } });

/** The unit's manual board pins, persisted server-side so a reload keeps
 *  them. GET answers {pins: {"wo:seq": {...}}}; PUT replaces the whole
 *  map — the board owns it and saves after every change. */
export const getPins = (unit) => api.get("/api/pins", { params: { unit } });
export const savePins = (unit, pins) =>
  api.put("/api/pins", { pins }, { params: { unit } });
